import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  DashboardWardSelection,
  IWardSelectOption,
} from "../../../../@types/admin";
import { SelectDropdown } from "../../../../components/SelectDropdown/SelectDropdown";
import { useWards } from "../../../../hooks/queries/wards";
import { getWardLabel } from "../../../../utils/wardLabel";

interface IWardFilterProps {
  value: DashboardWardSelection;
  onChange: (selection: DashboardWardSelection) => void;
}

export function WardFilter({ value, onChange }: IWardFilterProps) {
  const { t } = useTranslation();

  // GetAllWards, not GetAvailableWards: the dashboard behind this filter now
  // counts every ward an internal operator can read, so the filter has to
  // offer the same set or "All wards" would silently mean more than the list
  // it is drawn from. The endpoint applies the same EN-5094 split the counters
  // do — every published ward for a @patientmpower.com account, the caller's
  // own wards for anyone else — so this widens nothing for a hospital user.
  const { wards, isWardsLoading } = useWards();
  const { pluralLower } = getWardLabel();

  // The ward lists can emit rows with a null/0 hospitalId (the CASE expression
  // in GetAvailableWards returns NULL when no permission_level branch matches).
  // Drop those — otherwise they end up as `WardIds=0` on the dashboard
  // endpoints and trigger a 400 from the validator that requires WardIds > 0.
  const options = useMemo<IWardSelectOption[]>(
    () =>
      (wards ?? [])
        .filter((w) => Number.isFinite(w.wardId) && w.wardId > 0)
        .map((w) => ({
          value: String(w.wardId),
          label: w.wardName,
          key: `w-${w.wardId}`,
        })),
    [wards]
  );

  const selectedValues = useMemo(() => value.map(String), [value]);

  return (
    <SelectDropdown
      options={options}
      value={selectedValues}
      placeholder={
        isWardsLoading
          ? t("reports_wardsLoading", { wards: pluralLower })
          : t("reports_wardsAllPlaceholder", { wards: pluralLower })
      }
      width={320}
      height={40}
      multiple
      showSearch
      optionFilterProp="label"
      disabled={isWardsLoading}
      onValueChange={(selected) => {
        const raw = Array.isArray(selected) ? selected : [selected];
        const ids = raw
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n) && n > 0);
        onChange(ids);
      }}
    />
  );
}
